import React, { useState, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import {
  Flex,
  Grid,
  GridItem,
  Image,
  Button,
  Hide,
  Typography,
  Spinner,
} from '@pnld/components-web'

import LogoFNDE from '@/assets/svg/fnde/mono.svg'
import LogoPNLD from '@/assets/svg/pnld/mono.svg'
import LogoSPA from '@/assets/svg/spa/mono.svg'
import { ROUTES } from '@/config/routes'
import { useAuth } from '@/contexts/AuthContext'
import { stringToBoolean } from '@/utils/string'

const Login: React.FC = () => {
  const navigate = useNavigate()
  const [kidImage, setKidImage] = useState('')
  const { isAuthenticated, isLoading, loginWithKeycloak } = useAuth()

  const hasDevLogin = stringToBoolean(process.env.REACT_APP_DEV_LOGIN)

  const handleLoginKeycloak = () => {
    loginWithKeycloak()
  }

  const handleLoginDevelopment = () => {
    navigate(ROUTES.LOGIN_DEV)
  }

  useEffect(() => {
    const loadRandomKidImage = async () => {
      let totalImages = 0
      let keepLoading = true
      while (keepLoading) {
        try {
          await import(`@/assets/img/kids/kid-${totalImages}.png`)
          totalImages++
        } catch {
          keepLoading = false
        }
      }

      const storageKey = 'displayed-kid-images'

      let displayed: number[] = JSON.parse(
        localStorage.getItem(storageKey) ?? '[]'
      )

      if (displayed.length >= totalImages) {
        displayed = []
      }

      let randomIndex: number
      do {
        randomIndex = Math.floor(Math.random() * totalImages)
      } while (displayed.includes(randomIndex))

      const imageModule = await import(
        `@/assets/img/kids/kid-${randomIndex}.png`
      )
      setKidImage(imageModule.default)

      displayed.push(randomIndex)
      localStorage.setItem(storageKey, JSON.stringify(displayed))
    }

    loadRandomKidImage()
  }, [])

  if (isAuthenticated && !isLoading) {
    return <Navigate to={ROUTES.DEFAULT_AUTHENTICATED} replace />
  }

  if (isLoading) {
    return (
      <Flex align="center" justify="center" w="100" h="100vh">
        <Spinner size="xl" color="brand.highlight.dark_2" />
      </Flex>
    )
  }

  return (
    <Flex height="100vh">
      <Hide below="lg">
        <Flex
          direction="column"
          height="100%"
          backgroundColor="brand.primary.dark_2"
          overflow="hidden"
          flex={1}
        >
          <Grid templateColumns="1fr" templateRows="1fr 2fr" height="100%">
            <GridItem
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="flex-end"
              paddingX="8"
              paddingTop="16"
              paddingBottom="8"
            >
              <Flex direction="column" gap={4}>
                <Flex direction="row" gap={9} align="center">
                  <Image src={LogoFNDE} width="160px" />
                  <Image src={LogoPNLD} width="160px" />
                </Flex>
                <Typography
                  variant="d-sm"
                  color="brand.neutral.white"
                  textAlign="left"
                >
                  Transformando vidas
                  <br /> por meio da educação.
                </Typography>
              </Flex>
            </GridItem>
            <GridItem
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="flex-end"
              overflow="hidden"
            >
              <Image
                src={kidImage}
                height="100%"
                maxHeight="600px"
                objectFit="cover"
                objectPosition="top center"
              />
            </GridItem>
          </Grid>
        </Flex>
      </Hide>
      <Flex
        direction="column"
        justify="center"
        align="center"
        paddingX="8"
        flex={1}
        gap={16}
        backgroundColor="brand.highlight.dark"
      >
        <Image src={LogoSPA} width="300px" />
        <Flex direction="column" gap="4" width="full" maxWidth="24rem">
          <Button
            label="Entrar com gov.br"
            width="full"
            backgroundColor="brand.primary.dark_2"
            _hover={{ backgroundColor: 'brand.primary.dark_1' }}
            onClick={handleLoginKeycloak}
          />
          {hasDevLogin ? (
            <Button
              label="Entrar como desenvolvedor"
              width="full"
              backgroundColor="#A82E2F"
              _hover={{ backgroundColor: '#C63637' }}
              onClick={handleLoginDevelopment}
            />
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default Login
